/**
 * =========================================================
 * Modelo 130 Net Monthly Income Calculator
 * =========================================================
 *
 * Expected input:
 *
 * [
 *   {
 *     year: 2025,
 *     quarter: 4,              // 1 | 2 | 3 | 4
 *     annualizedNet: 24000     // Box 03 (YTD cumulative net)
 *   }
 * ]
 *
 * IMPORTANT:
 * annualizedNet here means OCR parsed Box 03 value
 * (NOT already annualized)
 *
 * =========================================================
 */

const IRPF_BRACKETS = [
  { lower: 0, upper: 12000, rate: 0.19 },
  { lower: 12000, upper: 21000, rate: 0.24 },
  { lower: 21000, upper: 35000, rate: 0.3 },
  { lower: 35000, upper: 60000, rate: 0.37 },
  { lower: 60000, upper: Infinity, rate: 0.45 },
];

const QUARTER_FACTORS = {
  1: 4 / 1,
  2: 4 / 2,
  3: 4 / 3,
  4: 4 / 4,
};

/**
 * Progressive IRPF calculation
 */
function calculateIRPF(annualIncome) {
  if (!annualIncome || annualIncome <= 0) {
    return 0;
  }

  let irpf = 0;

  for (const bracket of IRPF_BRACKETS) {
    const { lower, upper, rate } = bracket;

    if (annualIncome <= lower) {
      break;
    }

    const taxableAmount = Math.min(annualIncome, upper) - lower;

    irpf += taxableAmount * rate;
  }

  return irpf;
}

/**
 * Reliability calculator
 */
function getReliability(formsUsed) {
  if (!formsUsed?.length) {
    return "Low";
  }

  const quarters = formsUsed.map((f) => f.quarter);

  const hasQ4 = quarters.includes(4);
  const hasQ3 = quarters.includes(3);
  const hasQ2 = quarters.includes(2);

  if (formsUsed.length >= 2 && (hasQ4 || hasQ3)) {
    return "High";
  }

  if (hasQ4) {
    return "High";
  }

  if (hasQ3) {
    return "Medium-High";
  }

  if (hasQ2) {
    return "Medium";
  }

  return "Low";
}

/**
 * Main function
 */
function calculateModelo130Income(documents = []) {
  try {
    if (!Array.isArray(documents) || documents.length === 0) {
      return {
        success: false,
        monthlyIncome: 0,
        annualIncome: 0,
        reliability: "Low",
        formsUsed: [],
        flags: ["NO_DATA"],
      };
    }

    /**
     * =========================================================
     * Step 1: Validate + Clean
     * =========================================================
     */
    const validDocs = documents
      .filter((doc) => {
        return (
          doc &&
          Number.isFinite(doc.year) &&
          Number.isFinite(doc.quarter) &&
          doc.quarter >= 1 &&
          doc.quarter <= 4 &&
          doc.annualizedNet !== null &&
          doc.annualizedNet !== undefined &&
          Number.isFinite(Number(doc.annualizedNet))
        );
      })
      .map((doc) => ({
        year: Number(doc.year),
        quarter: Number(doc.quarter),
        annualizedNet: Number(doc.annualizedNet),
      }));

    if (validDocs.length === 0) {
      return {
        success: false,
        monthlyIncome: 0,
        annualIncome: 0,
        reliability: "Low",
        formsUsed: [],
        flags: ["NO_VALID_DOCUMENTS"],
      };
    }

    /**
     * =========================================================
     * Step 2: Sort Latest First
     * =========================================================
     */
    validDocs.sort((a, b) => {
      if (b.year !== a.year) {
        return b.year - a.year;
      }

      return b.quarter - a.quarter;
    });

    /**
     * =========================================================
     * Step 3: Group by year
     * =========================================================
     */
    const groupedByYear = {};

    for (const doc of validDocs) {
      if (!groupedByYear[doc.year]) {
        groupedByYear[doc.year] = [];
      }

      groupedByYear[doc.year].push(doc);
    }

    /**
     * =========================================================
     * Step 4: Keep ONLY latest 2 years
     * =========================================================
     */
    const latestYears = Object.keys(groupedByYear)
      .map(Number)
      .sort((a, b) => b - a)
      .slice(0, 2);

    /**
     * =========================================================
     * Step 5: For each year, keep ONLY highest quarter
     * =========================================================
     */
    const selectedForms = [];

    for (const year of latestYears) {
      const docs = groupedByYear[year];

      docs.sort((a, b) => b.quarter - a.quarter);

      // highest quarter only
      selectedForms.push(docs[0]);
    }

    /**
     * =========================================================
     * Step 6: Calculate annual after-tax income
     * =========================================================
     */
    const yearlyResults = [];
    const flags = [];

    for (const form of selectedForms) {
      const { year, quarter, annualizedNet } = form;

      /**
       * Negative income edge case
       */
      if (annualizedNet <= 0) {
        yearlyResults.push({
          year,
          quarter,
          annualizedNet: 0,
          irpf: 0,
          afterTaxAnnual: 0,
        });

        flags.push(`NEGATIVE_OR_ZERO_INCOME_${year}_Q${quarter}`);

        continue;
      }

      /**
       * Annualize based on quarter
       */
      const factor = QUARTER_FACTORS[quarter] || 1;

      const projectedAnnualIncome = annualizedNet * factor;

      /**
       * Calculate progressive IRPF
       */
      const irpf = calculateIRPF(projectedAnnualIncome);

      /**
       * After-tax annual
       */
      const afterTaxAnnual = projectedAnnualIncome - irpf;

      yearlyResults.push({
        year,
        quarter,
        factor,
        originalBox03: annualizedNet,
        projectedAnnualIncome,
        irpf,
        afterTaxAnnual,
      });
    }

    /**
     * =========================================================
     * Step 7: Average across years
     * =========================================================
     */
    const validAnnuals = yearlyResults.map((y) => y.afterTaxAnnual);

    const averagedAnnualIncome =
      validAnnuals.reduce((sum, val) => sum + val, 0) / validAnnuals.length;

    const estimatedMonthlyIncome = averagedAnnualIncome / 12;

    /**
     * =========================================================
     * Final response
     * =========================================================
     */
    return {
      success: true,

      monthlyIncome: Number(estimatedMonthlyIncome.toFixed(2)),

      annualIncome: Number(averagedAnnualIncome.toFixed(2)),

      reliability: getReliability(selectedForms),

      formsUsed: selectedForms.map((f) => ({
        year: f.year,
        quarter: f.quarter,
      })),

      yearlyBreakdown: yearlyResults,

      flags,
    };
  } catch (error) {
    return {
      success: false,
      monthlyIncome: 0,
      annualIncome: 0,
      reliability: "Low",
      formsUsed: [],
      flags: ["CALCULATION_ERROR"],
      error: error.message,
    };
  }
}

export { calculateModelo130Income };

// /**
//  * =========================================================
//  * Example Usage
//  * =========================================================
//  */

// const docs = [
//   {
//     year: 2025,
//     quarter: 4,
//     annualizedNet: 24000,
//   },
//   {
//     year: 2025,
//     quarter: 3,
//     annualizedNet: 18000,
//   },
//   {
//     year: 2026,
//     quarter: 2,
//     annualizedNet: 8000,
//   },
// ];

// const result = calculateModelo130Income(docs);

// console.log(result);

// /**
//  * Expected:
//  *
//  * - Uses ONLY:
//  *    2025 Q4
//  *    2026 Q2
//  *
//  * - Computes after-tax annual for both
//  * - Averages them
//  * - Returns estimated monthly income
//  */
